import { useEffect, useState } from 'react';
import { IconDeviceFloppy } from '@tabler/icons-react';
import { showToast } from '../components/ui/Toast';
import { useSiteContent } from '../context/SiteContentContext';
import { fetchPromoCodes } from '../lib/storeData';
import { readEmailSettings, validateEmailSettings } from '../lib/emailSettings';

function todayInGuyana() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Guyana' });
}

// Admin > Emails: when the abandoned-cart reminders and the delivery
// follow-up go out. Saved to site_content.email_settings; the
// send-scheduled-emails function (every 15 minutes) reads it.
// usesPromo: true when the 24-hour template mentions {promo_code}.
export default function EmailSettings({ usesPromo }) {
  const { content, save } = useSiteContent();
  const savedSettings = content.email_settings;
  const [form, setForm] = useState(() => readEmailSettings(savedSettings));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [promos, setPromos] = useState([]);
  const [promoError, setPromoError] = useState(null);

  useEffect(() => {
    if (!dirty) setForm(readEmailSettings(savedSettings));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedSettings]);

  useEffect(() => {
    let cancelled = false;
    fetchPromoCodes().then(res => {
      if (cancelled) return;
      setPromos(res.promos || []);
      setPromoError(res.error);
    });
    return () => { cancelled = true; };
  }, []);

  function set(key, value) {
    setForm(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  }

  async function saveSettings() {
    if (saving) return;
    const v = validateEmailSettings(form);
    if (v.error) { showToast(v.error, 'error'); return; }
    setSaving(true);
    const r = await save({ email_settings: v.settings });
    setSaving(false);
    if (r && r.error) { showToast(r.error, 'error'); return; }
    setDirty(false);
    showToast('Reminder settings saved');
  }

  const today = todayInGuyana();
  const usable = promos.filter(p => p.is_active && (!p.expires_on || p.expires_on >= today));
  const options = usable.map(p => ({ code: p.code, label: `${p.code} (${p.discount_percent}% off)` }));
  const code = form.abandoned_promo_code || '';
  if (code && !options.some(o => o.code === code)) {
    options.push({ code, label: `${code} (turned off or expired: not sent)` });
  }
  const codeUsable = code && usable.some(p => p.code === code);
  const promoWarning = form.abandoned_enabled && usesPromo && !codeUsable
    ? 'Your 24-hour email mentions {promo_code}, but no working code is chosen, so customers would see a blank. Choose a code, or take {promo_code} out of that email below.'
    : null;

  const numberInput = (id, key, min, max) => (
    <input id={id} className="adm-input" type="number" inputMode="numeric" min={min} max={max} step={1}
      value={form[key]} onChange={e => set(key, e.target.value)} style={{ width: 90 }} />
  );

  return (
    <section className="adm-card" aria-labelledby="es-title" style={{ marginBottom: 20 }}>
      <div className="adm-card-title" id="es-title">Reminder settings</div>
      <p style={{ fontSize: 12, color: '#777', margin: '0 0 14px', lineHeight: 1.5 }}>
        Reminders only go to customers who ticked &ldquo;Email me a reminder&rdquo; at checkout, at most 2 per checkout, and never after they&rsquo;ve paid or unsubscribed. The shop checks every 15 minutes.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20 }}>
        <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, fontWeight: 600, marginBottom: 10 }}>
            <input type="checkbox" checked={form.abandoned_enabled} onChange={e => set('abandoned_enabled', e.target.checked)} style={{ accentColor: '#1E3A2F' }} />
            Send abandoned-cart reminders
          </label>
          <div className="adm-field" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label className="adm-label" htmlFor="es-h1" style={{ margin: 0, minWidth: 130 }}>First reminder after</label>
            {numberInput('es-h1', 'abandoned_1_hours', 1, 72)}<span style={{ fontSize: 12, color: '#777' }}>hours</span>
          </div>
          <div className="adm-field" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label className="adm-label" htmlFor="es-h2" style={{ margin: 0, minWidth: 130 }}>Second reminder after</label>
            {numberInput('es-h2', 'abandoned_2_hours', 2, 168)}<span style={{ fontSize: 12, color: '#777' }}>hours</span>
          </div>
          <div className="adm-field">
            <label className="adm-label" htmlFor="es-promo">Promo code in the second reminder</label>
            <select id="es-promo" className="adm-select" value={code} onChange={e => set('abandoned_promo_code', e.target.value || null)}>
              <option value="">No code</option>
              {options.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
            </select>
            {promoError && <div style={{ fontSize: 11, color: '#9A3412', marginTop: 4 }}>Couldn&rsquo;t load your promo codes. Make them under Promo codes.</div>}
          </div>
          {promoWarning && (
            <p role="note" style={{ fontSize: 12, color: '#9A3412', background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 6, padding: '8px 10px', lineHeight: 1.5, margin: 0 }}>
              {promoWarning}
            </p>
          )}
        </fieldset>

        <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, fontWeight: 600, marginBottom: 10 }}>
            <input type="checkbox" checked={form.followup_enabled} onChange={e => set('followup_enabled', e.target.checked)} style={{ accentColor: '#1E3A2F' }} />
            Send a follow-up after delivery
          </label>
          <div className="adm-field" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label className="adm-label" htmlFor="es-days" style={{ margin: 0, minWidth: 130 }}>Send it after</label>
            {numberInput('es-days', 'followup_days', 1, 60)}<span style={{ fontSize: 12, color: '#777' }}>days</span>
          </div>
          <p style={{ fontSize: 11, color: '#888', lineHeight: 1.5, margin: 0 }}>Counted from when you click &ldquo;Mark delivered&rdquo; on the order.</p>
        </fieldset>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginTop: 14 }}>
        <span className={`adm-save-status ${dirty ? '' : 'adm-save-status--saved'}`}>{dirty ? '● Unsaved' : '✓ Saved'}</span>
        <button className="adm-btn" onClick={saveSettings} disabled={saving}><IconDeviceFloppy size={14} /> Save settings</button>
      </div>
    </section>
  );
}
